import Descriptor from '@r6digital/rapidstor-types/react/entities/Descriptor'; // descriptorTransformer,
import Location, {
    paymentGatewayTransformer,
} from '@r6digital/rapidstor-types/react/entities/Location';
import { unitTypeTransformer } from '@r6digital/rapidstor-types/react/entities/UnitType';
import { UnitList } from '@r6digital/rapidstor-types/react/rtk-query/UnitList';
import { RapidStorUnitList } from '@r6digital/rapidstor-types/types/Api';
import { PostProcessedDescriptor } from '@r6digital/rapidstor-types/types/Descriptor';
import Persona from '@r6digital/rapidstor-types/types/Persona';
import { BaseReservation } from '@r6digital/rapidstor-types/types/Reservation';
import Site from '@r6digital/rapidstor-types/types/Site';
import { createEntityAdapter, createSelector } from '@reduxjs/toolkit';
import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { EntityState } from '@reduxjs/toolkit/src/entities/models';

import store, { RootState } from '../../store';

import {
    options,
    RapidStorReservation,
    RapidStorReservationRequest,
    RapidStorResponse,
    RapidStorUnitListRequest,
} from '../../services/rapidstor';
import defaultLocations from './defaultLocations';

export const descriptorMinArea = (descriptor: PostProcessedDescriptor): number => {
    const sizeCriteria = descriptor.criteria?.include?.sizes ?? [];
    return Math.min(
        ...descriptor.unitTypes.map((u) => u.dcArea),
        ...sizeCriteria.map((s) => s.width * s.length)
    );
};

export const descriptorTransformer = (
    descriptor: PostProcessedDescriptor,
    location?: Location
): Descriptor => {
    return {
        _id: descriptor._id as string,
        locationCode: descriptor.sLocationCode,
        corpCode: descriptor.sCorpCode,
        name: descriptor.name,
        description: descriptor.description,
        descriptions: descriptor.descriptions,
        tags: descriptor.tags,
        picture: descriptor.picture,
        slides: descriptor.slides,
        defaultInsuranceCoverage: descriptor.defaultInsuranceCoverage,
        deals: descriptor.deals,
        enabled: descriptor.enabled,
        hidden: descriptor.hidden,
        unitTypes: descriptor.unitTypes.map((u) => unitTypeTransformer(u, location)),
        ordinalPosition: descriptor.ordinalPosition,
        area: descriptorMinArea(descriptor),
        specialText: descriptor.specialText,
        upgradesTo: descriptor.upgradesTo,
        useForCarousel: descriptor.useForCarousel,
    };
};

export const formatAddress = (location: Location): string => {
    const { address1, address2, city, region, postalCode } = location.address;

    return [
        address1 ? `${address1}, ` : '',
        address2 ? `${address2}, ` : '',
        city ? `${city}, ` : '',
        region ? `${region}, ` : '',
        postalCode ? postalCode : '',
    ].join('');
};

export const locationTransformer = (location: Site): Location => {
    const name = location.sSiteName.replace(/^U-Store-it (- )?/i, '');
    return <Location>{
        name: location.sSiteName,
        slug: name
            .toLowerCase()
            .trim()
            .replace(/[\s_'-]+/g, '-')
            .replace(/[^\w\s-]/g, '')
            .replace(/^-+|-+$/g, ''),
        locationId: location.SiteID,
        locationCode: location.sLocationCode,
        corpCode: location.sCorpCode,
        contactName: location.sContactName,
        contactEmail: location.sEmailAddress,
        contactPhone: location.sSitePhone,
        websiteUrl: location.sWebSiteURL,
        latitude: parseFloat(location.dcLatitude),
        longitude: parseFloat(location.dcLongitude),
        timezoneOffset: location.iGMTTimeOffset,
        address: {
            address1: location.sSiteAddr1,
            address2: location.sSiteAddr2,
            city: location.sSiteCity,
            region: location.sSiteRegion,
            postalCode: location.sSitePostalCode,
            country: location.sSiteCountry,
        },
        rapidstor: {
            allowMoveIn: location.rapidstor.allowMoveIn,
            allowReservation: location.rapidstor.allowReservation,
            allowSaveQuote: location.rapidstor.allowSaveQuote,
            allowedCreditCardTypes: {
                visa: location.rapidstor.allowedCreditCardTypes.visa,
                mastercard: location.rapidstor.allowedCreditCardTypes.mastercard,
                amex: location.rapidstor.allowedCreditCardTypes.amex,
            },
            areaSizeSymbol: location.rapidstor.areaSizeSymbol,
            completeMoveinUrl: location.rapidstor.completeMoveinURL,
            reservationNumberOfDaysInAdvanceLimit:
                location.rapidstor.reservationNumberOfDaysInAdvanceLimit,
            reservationNumberOfDaysInAdvanceMinimum:
                location.rapidstor.reservationNumberOfDaysInAdvanceMinimum,
            reservationNumberOfDaysInAdvanceChargeFee:
                location.rapidstor.reservationNumberOfDaysInAdvanceChargeFee,
            moveinNumberOfDaysInAdvanceLimit:
                location.rapidstor.moveinNumberOfDaysInAdvanceLimit,
            includeReservedUnitsInOccupied:
                location.rapidstor.includeReservedUnitsInOccupied,
            country: location.rapidstor.country,
            currency: location.rapidstor.currency,
            currencySymbol: location.rapidstor.currencySymbol,
            dateFormat: location.rapidstor.dateFormat,
            mediumLargeSizes: location.rapidstor.mediumLargeSize,
            smallMediumSizes: location.rapidstor.smallMediumSize,
            pushRate: location.rapidstor.pushRate,
            showWeeklyRates: location.rapidstor.showWeeklyRates,
            siteLogo: location.rapidstor.siteLogo,
            sizeSymbol: location.rapidstor.sizeSymbol,
            hideUnitsMinAvailable: location.rapidstor.hideUnitsMinAvailable,
            minAvailableUnits: location.rapidstor.minAvailableUnits,
            useDescriptorTitle: location.rapidstor.useDescriptorTitle,
            showPricesButtonText: location.rapidstor.showPricesButtonText,
            quickReserveButtonText: location.rapidstor.quickReserveButtonText,
            payNowButtonText: location.rapidstor.payNowButtonText,
            payLaterButtonText: location.rapidstor.payLaterButtonText,
            showPricesShowNeeded: location.rapidstor.showPricesShowNeeded,
            showPricesCreatesLead: location.rapidstor.showPricesCreatesLead,
            showPricesBehaviour: {
                createLead: location.rapidstor.showPricesBehaviour.createLead,
                sendEmail: location.rapidstor.showPricesBehaviour.sendEmail,
            },
            tenantAccounts: location.rapidstor.tenantAccounts,
            hidePrices: location.rapidstor.hidePrices,
            hideStandardPrices: location.rapidstor.hideStandardPrices,
            noAvailableUnitsText: location.rapidstor.noAvailableUnitsText,
            saveQuoteButtonText: location.rapidstor.saveQuoteButtonText,
            saveQuotePopupText: location.rapidstor.saveQuotePopupText,
            useDealCoupons: location.rapidstor.useDealCoupons,
            useBoxShopCoupons: location.rapidstor.useBoxShopCoupons,
            pickupEnabled: location.rapidstor.pickupEnabled,
            legacyPickupLocationsEnabled: location.rapidstor.legacyPickupLocationsEnabled,
            legacyPickupLocations: location.rapidstor.legacyPickupLocations,
            wizards: {
                moveinSingle: location.rapidstor.wizards.moveinSingle,
            },
            paymentGateways: paymentGatewayTransformer(location),
            useAutoBilling: location.rapidstor.useAutoBilling,
            autoBillCheckedByDefault: location.rapidstor.autoBillCheckedByDefault,
            requireAutobill: location.rapidstor.requireAutobill,
            creditCardForAutobill: {
                enabled: location.rapidstor.creditCardForAutobill.enabled,
            },
            directDebit: {
                enabled: location.rapidstor.directDebit.enabled,
                creditAmount: location.rapidstor.directDebit.creditAmount,
            },
            datesClosed: location.rapidstor.datesClosed,
            siteOpeningDate: location.rapidstor.siteOpeningDate,
            carouselListing: location.administrative.carouselListing,
            moveinRedirectUrl: location.rapidstor.moveinRedirectUrl,
            forceInitialCard: location.rapidstor.forceInitialCard,
        },
        scaConfiguration: location.scaConfiguration,
        deleted: location.deleted,
        archived: location.bArchived == 'true',
        rapidstorEnabled: location.rapidstorEnabled,
        token: location.token,
        createdAt: location.dCreated,
        updatedAt: location.dUpdated,
        settings: {
            logo: location.rapidstor.siteLogo,
            groundFloor: location.rapidstor.groundFloor || 0,
            groupUnitsByFloor: location.rapidstor.unitListing.useMultilevel,
            billingDefault: location.rapidstor.billingDefault,
            sizeSymbol: location.rapidstor.sizeSymbol,
            areaSizeSymbol: location.rapidstor.areaSizeSymbol,
            allowMoveIn: location.rapidstor.allowMoveIn,
            showAreaInsteadOfDimensions: location.rapidstor.showAreaInsteadOfDimensions,
            webhooks: location.rapidstor.webhooks,
            marketingSurvey: location.rapidstor.marketingSurvey,
            unitSortOrder: [
                location.rapidstor.unitListing.sortOrder.primary,
                location.rapidstor.unitListing.sortOrder.secondary,
                location.rapidstor.unitListing.sortOrder.tertiary,
            ].filter((sortOrder) => sortOrder !== 'none'),
        },
        skin: {
            accentColours: {
                primary: location.skin.accentColours.primary,
            },
            defaultUnitPicture: location.skin.defaultUnitPicture,
            mainColour: location.skin.mainColour,
            highlightColour: location.skin.highlightColour,
            themeColour: location.skin.themeColour,
            moveinTimeRemaining: location.skin.moveinTimeRemaining,
        },
        hours: location.hours,
    };
};

const locationsAdapter = createEntityAdapter<Location>({
    selectId: (location) => location.locationCode,
    sortComparer: (a, b) => a.name.localeCompare(b.name),
});

const locationInitialState = locationsAdapter.getInitialState({
    ids: [],
    entities: defaultLocations
        .filter((location) => location.rapidstorEnabled)
        .map((location) => locationTransformer(location)),
});

const baseQuery = fetchBaseQuery({
    baseUrl: options.baseURL,
});

const CorpCode = 'CRAG';

export const rapidstorApi = createApi({
    reducerPath: 'rapidstorApi',
    baseQuery: baseQuery,
    keepUnusedDataFor: 3600,
    tagTypes: ['Location', 'Questionnaire', 'UnitList'],
    endpoints: (builder) => ({
        getLocations: builder.query<EntityState<Location>, void>({
            query: () => ({
                url: '/api/v1/locations',
                method: 'GET',
                headers: {
                    corp: CorpCode,
                },
            }),
            providesTags: ['Location'],
            transformResponse: (resp: RapidStorResponse<Site[]>) => {
                const locations = resp.results
                    .map((location) => locationTransformer(location))
                    .filter((location) => location.rapidstorEnabled)
                    .filter((location) => location.locationCode !== 'LSETUPS');

                return locationsAdapter.setAll(locationInitialState, locations);
            },
        }),
        getPersonas: builder.query<Persona[], string>({
            query: (locationCode) => ({
                url: `/api/v1/personas`,
                method: 'GET',
                headers: {
                    corp: CorpCode,
                    site: locationCode,
                },
            }),
            transformResponse: (resp: RapidStorResponse<Persona[]>) => {
                return resp.results;
            },
        }),
        getUnitList: builder.query<UnitList, RapidStorUnitListRequest>({
            query: ({ locationCode, personaId }) => ({
                url: `/api/v1/locations/unit-list${
                    personaId ? `?persona=${personaId}` : ''
                }`,
                method: 'GET',
                headers: {
                    corp: CorpCode,
                    site: locationCode,
                },
            }),
            providesTags: (result) => {
                return result
                    ? [{ type: 'UnitList', id: result.unitTypes[0].locationCode }]
                    : [];
            },
            transformResponse: (resp: RapidStorResponse<RapidStorUnitList>): UnitList => {
                return {
                    descriptors: resp.results.descriptors
                        .map((d): Descriptor => {
                            const location: Location | undefined = selectLocationByCode(
                                store.getState(),
                                d.sLocationCode
                            );
                            return descriptorTransformer(d, location);
                        })
                        .sort((a, b) => a.ordinalPosition - b.ordinalPosition)
                        .filter((d) => d.unitTypes.length > 0 && !d.hidden && d.enabled),
                    unitTypes: resp.results.unitTypes.map((u) => {
                        const location: Location | undefined = selectLocationByCode(
                            store.getState(),
                            u.sLocationCode
                        );
                        return unitTypeTransformer(u, location);
                    }),
                };
            },
        }),
        getReservation: builder.query({
            query: ({ locationCode, token }) => ({
                url: `/api/v1/reservation/${token}`,
                method: 'GET',
                headers: {
                    site: locationCode,
                    corp: CorpCode,
                },
            }),
            transformResponse: (resp: RapidStorResponse<RapidStorReservation>) => {
                return resp.results;
            },
        }),
        postReservation: builder.mutation<BaseReservation, RapidStorReservationRequest>({
            query: ({ locationCode, payload }) => ({
                url: `/api/v1/reservation`,
                method: 'POST',
                body: payload,
                headers: {
                    site: locationCode,
                    corp: CorpCode,
                },
            }),
            transformResponse: (resp: RapidStorResponse<BaseReservation>) => {
                return resp.results;
            },
        }),
        postSaveQuote: builder.mutation<BaseReservation, RapidStorReservationRequest>({
            query: ({ locationCode, payload }) => ({
                url: `/api/v1/reservation/save-quote`,
                method: 'POST',
                body: payload,
                headers: {
                    site: locationCode,
                    corp: CorpCode,
                },
            }),
            transformResponse: (resp: RapidStorResponse<BaseReservation>) => {
                return resp.results;
            },
        }),
    }),
});

const selectLocationsResult = rapidstorApi.endpoints.getLocations.select();
const selectLocationData = createSelector(selectLocationsResult, (locationResult) => {
    if (locationResult.status !== 'fulfilled') {
        return locationsAdapter.setAll(
            locationInitialState,
            defaultLocations.map((l) => locationTransformer(l))
        );
    }
    return locationResult.data;
});

export const { selectAll: selectAllLocations, selectById: selectLocationByCode } =
    locationsAdapter.getSelectors(
        (state: RootState) => selectLocationData(state) ?? locationInitialState
    );

export const selectLocationsByState = createSelector(
    [selectAllLocations, (state, region?: string) => region],
    (locations, region) =>
        locations.filter((location) =>
            region ? location.address.region === region : true
        )
);

export const selectLocationBySlug = createSelector(
    [selectAllLocations, (state, slug: string) => slug],
    (locations, slug) => locations.find((location) => location.slug === slug)
);

export const {
    useGetLocationsQuery,
    useGetUnitListQuery,
    useGetReservationQuery,
    usePostReservationMutation,
    usePostSaveQuoteMutation,
    useGetPersonasQuery,
} = rapidstorApi;
